import {
  REVIEW_RATING,
  type ReviewRating,
} from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";

import type { LearnCardItemKind } from "#learn-card-item";

/**
 * Everything a learner can DO to the current card. One vocabulary for every input: the floating
 * buttons, a horizontal swipe on either face, a rating shortcut key, a voice command, and the
 * fast-forward path all produce one of these, and the same pipeline executes it.
 *
 *   * `rate` — grade a review card. Applies to grapheme drills and, for a sentence-production
 *     drill, to the word under the cursor (see `sentenceDrillCommitReviews`).
 *   * `accept-lesson` — enroll the new character and move on. This is the ONLY thing that enrolls;
 *     revealing or reading the mnemonics never does.
 *   * `defer-lesson` — "skip until tomorrow": persist a deferral to the next local day and move on
 *     without enrolling or rating.
 *   * `continue` — advance with no domain write at all. A rating-less card (no in-study card
 *     resolves for the drill), a voice "skip", or a completed slide.
 *   * `answer-quiz` — commit a checked quiz answer: correct reviews the quiz Easy, wrong Again.
 *     The first answer (in its lesson) also enrolls the quiz. A quiz is never rated or continued.
 */
export type LearnCardAction =
  | { kind: "rate"; rating: ReviewRating }
  | { correct: boolean; kind: "answer-quiz" }
  | { kind: "accept-lesson" }
  | { kind: "defer-lesson" }
  | { kind: "mark-known" }
  | { kind: "skip-lesson" }
  | { kind: "continue" };

/** Where the action came from; decides the exit animation for actions with more than one entry. */
export type LearnCardActionTrigger = "button" | "fast-forward" | "keyboard" | "swipe" | "voice";

export type LearnCardExitDirection = "left" | "right" | "up";

/**
 * The direction the outgoing card leaves in. Kept separate from the rating itself: Good and Easy
 * share a rightward exit, so the direction can never be used to recover the rating.
 *
 * Review buttons and swipes agree: Again left, Hard up, Good/Easy right.
 * Lesson Next Card exits upward; mark-known exits left and defer exits right.
 *
 * A quiz answer leaves like the rating it stands for: correct right (Easy), wrong left (Again).
 *
 * `up` is also the neutral exit for actions a button reaches without taking a side. Next Card is
 * bound to BOTH horizontal swipes, so dismissing the button press left or right would imply a
 * direction the learner never chose; the card leaves under the header instead.
 */
export function learnCardExitDirectionForAction(action: LearnCardAction): LearnCardExitDirection {
  switch (action.kind) {
    case "rate": {
      switch (action.rating) {
        case REVIEW_RATING.Again: {
          return "left";
        }
        case REVIEW_RATING.Good:
        case REVIEW_RATING.Easy: {
          return "right";
        }
        default: {
          return "up";
        }
      }
    }
    case "defer-lesson": {
      return "right";
    }
    case "skip-lesson": {
      return "right";
    }
    case "mark-known": {
      return "left";
    }
    case "answer-quiz": {
      return action.correct ? "right" : "left";
    }
    case "accept-lesson":
    case "continue": {
      return "up";
    }
  }
}

/** Whether two actions write the same thing (a rating compares by its grade). */
export function isSameLearnCardAction(a: LearnCardAction, b: LearnCardAction): boolean {
  if (a.kind === "rate" && b.kind === "rate") {
    return a.rating === b.rating;
  }
  if (a.kind === "answer-quiz" && b.kind === "answer-quiz") {
    return a.correct === b.correct;
  }
  return a.kind === b.kind;
}

/**
 * The action a committed horizontal swipe means for this kind of card. A review swipe rates
 * Again or Easy; either lesson swipe accepts, like Next Card. A slide swipe advances, like Continue.
 * A quiz has no swipe of its own: once checked, either swipe commits the answer it was graded with,
 * which only the quiz card knows (see the deck controller).
 */
export function learnCardActionForHorizontalSwipe(
  itemKind: LearnCardItemKind,
  direction: "left" | "right",
): LearnCardAction | null {
  switch (itemKind) {
    case "review": {
      return {
        kind: "rate",
        rating: direction === "left" ? REVIEW_RATING.Again : REVIEW_RATING.Easy,
      };
    }
    case "lesson": {
      return { kind: "accept-lesson" };
    }
    case "slide": {
      return { kind: "continue" };
    }
    case "quiz": {
      return null;
    }
  }
}

/** Whether an action makes sense for a card of this kind (a rating on a lesson never does). */
export function isLearnCardActionAllowedFor(
  itemKind: LearnCardItemKind,
  action: LearnCardAction,
): boolean {
  switch (action.kind) {
    case "rate": {
      return itemKind === "review";
    }
    case "accept-lesson":
    case "mark-known":
    case "skip-lesson":
    case "defer-lesson": {
      return itemKind === "lesson";
    }
    case "answer-quiz": {
      return itemKind === "quiz";
    }
    case "continue": {
      // A quiz is passed only by answering it.
      return itemKind !== "quiz";
    }
  }
}

const RATING_LABEL: Record<ReviewRating, string> = {
  [REVIEW_RATING.Again]: "Again",
  [REVIEW_RATING.Easy]: "Easy",
  [REVIEW_RATING.Good]: "Good",
  [REVIEW_RATING.Hard]: "Hard",
};

/** The short label the action is known by in the UI ("Easy", "Skip until tomorrow", …). */
export function learnCardActionLabel(action: LearnCardAction): string {
  switch (action.kind) {
    case "rate": {
      return RATING_LABEL[action.rating];
    }
    case "accept-lesson": {
      return "Next";
    }
    case "mark-known": {
      return "Mark Known";
    }
    case "skip-lesson": {
      return "Skip";
    }
    case "defer-lesson": {
      return "Skip until tomorrow";
    }
    case "continue":
    case "answer-quiz": {
      return "Continue";
    }
  }
}

export type LearnCardActionStatusPhase =
  /** The domain write for the action is in flight. */
  | "persisting"
  /** The write landed; the successor is still being prepared. */
  | "loading-next"
  /** The write landed but the successor could not be prepared; a retry is offered. */
  | "next-failed";

/**
 * What the parked card says while a committed action waits. Names the ACTUAL action — Good and
 * Easy get distinct labels even though they exit the same way — so the learner can see what they
 * committed to while it saves.
 */
export function learnCardActionStatusLabel(
  action: LearnCardAction,
  phase: LearnCardActionStatusPhase,
): string {
  switch (phase) {
    case "persisting": {
      return persistingLabel(action);
    }
    case "loading-next": {
      return `${savedLabel(action)} · Loading next card…`;
    }
    case "next-failed": {
      return `${savedLabel(action)} · Couldn't load the next card`;
    }
  }
}

function persistingLabel(action: LearnCardAction): string {
  switch (action.kind) {
    case "mark-known": {
      return "Marking as known…";
    }
    case "skip-lesson": {
      return "Skipping card…";
    }
    case "rate": {
      return `Marking as ${learnCardActionLabel(action)}…`;
    }
    case "accept-lesson": {
      return "Adding to study…";
    }
    case "defer-lesson": {
      return "Skipping until tomorrow…";
    }
    case "continue": {
      return "Loading next card…";
    }
    case "answer-quiz": {
      return "Saving answer…";
    }
  }
}

function savedLabel(action: LearnCardAction): string {
  switch (action.kind) {
    case "mark-known": {
      return "Marked known";
    }
    case "skip-lesson": {
      return "Skipped";
    }
    case "rate": {
      return `${learnCardActionLabel(action)} saved`;
    }
    case "accept-lesson": {
      return "Added";
    }
    case "defer-lesson": {
      return "Skipped until tomorrow";
    }
    case "continue": {
      return "Continuing";
    }
    case "answer-quiz": {
      return "Answer saved";
    }
  }
}
